import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "../assets/monthlyInspectionCreate.module.css";
import propertyMonthlyInspectionsService from "../services/propertyMonthlyInspectionsService";
import PhotoUpload from "../components/PhotoUpload";
import PhotoGallery from "../components/PhotoGallery";
import ChecklistGroup from "../components/ChecklistGroup";
import TicketsTable from "../components/TicketsTable";
import SignatureCanvas from "../components/SignatureCanvas";
import { validateAndShowErrors } from "../utils/validation";
import {
  saveDraft,
  deleteDraft,
  getResumableDraftByReport,
  OFFLINE_PHOTO_PREFIX,
} from "../lib/offlineDb";
import { triggerSync } from "../lib/inspectionSyncEngine";
import {
  METER_PHOTO_POOL,
  METER_DEFAULT_NAMES,
  DEFAULT_METER_SLOTS,
} from "../constants/meterPhotos";
import { usesNewMeters } from "../utils/meterCutover";
import propertyMetersService from "../services/propertyMetersService";

const MonthlyInspectionEdit = () => {
  const { propertyId, reportId } = useParams();
  const navigate = useNavigate();

  // State management
  const [property, setProperty] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isMetropolitanHousing, setIsMetropolitanHousing] = useState(false); // MTVH properties have different layout
  const [ticketTypes, setTicketTypes] = useState([]); // Services for MTVH checklist ticket type dropdown
  const [monthlyInspectionResponses, setMonthlyInspectionResponses] = useState(null); // Canned responses for MTVH notes dropdown

  // Large image viewer (per-report — Q2 in PHOTO_MODULE_CLIENT_QUESTIONS.md).
  // Inspection photos and meter photos use separate galleries so prev/next
  // navigation stays contained to its own section.
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [meterGalleryOpen, setMeterGalleryOpen] = useState(false);
  const [meterGalleryIndex, setMeterGalleryIndex] = useState(0);

  // Photo rotation state keyed by field name (e.g. image_1, image_5_a).
  // Lifted out of PhotoUpload so the lightbox and the inline thumbnail stay in
  // sync — rotating in either place updates the other. Visual only; the form
  // submit does not currently persist rotation for inspection photos.
  const [photoRotations, setPhotoRotations] = useState({});

  const rotatePhotoField = (field) => {
    setPhotoRotations((prev) => ({
      ...prev,
      [field]: ((prev[field] || 0) + 90) % 360,
    }));
  };

  // Local data-URL previews for freshly-uploaded photos. Lets the lightbox
  // display the same image the thumbnail shows without waiting on CDN
  // propagation or relying on a backend proxy.
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState({});

  const handlePhotoPreviewReady = useCallback((field, dataUrl) => {
    setPhotoPreviewUrls((prev) => {
      const next = { ...prev };
      if (dataUrl) {
        next[field] = dataUrl;
      } else {
        delete next[field];
      }
      return next;
    });
  }, []);

  // Form data - only include fields that exist in database schema
  const [formData, setFormData] = useState({
    // Required fields (must be set to valid values)
    type_id: 1, // Standard type
    property_id: parseInt(propertyId) || 0,
    month: 1, // Required field
    year: new Date().getFullYear(), // Required field
    status_id: 0, // Unpublished by default
    
    // Optional fields
    month_end: 0,
    is_weekly: 0,
    brief_overview: "",
    inspection_date: "",
    lease_start_date: "",
    
    // JSON fields
    // Meters (Oct 2026+): reading per property meter, keyed by meter id.
    meter_readings: {},

    // Meters (legacy, Sep 2026 and earlier)
    reading_fields: [],
    reading_fields_names: [],
    reading_fields_serials: [],
    
    // Other fields
    client: "",
    client_contact: "",
    property_id_name: "",
    address_2: "",
    postcode: "",
    guardians_number: "",
    account_manager: "",
    account_manager_email: "",
    client_contact_short: "",
    address_1: "",
    address_3: "",
    town: "",
    savings_made: "",
    months_under_management: "",
    
    // Photo fields - Standard (4 photos)
    image_1: null,
    image_1_name: "",
    image_2: null,
    image_2_name: "",
    image_3: null,
    image_3_name: "",
    image_4: null,
    image_4_name: "",
    image_5: null,
    image_5_name: "",
    image_6: null,
    image_6_name: "",
    image_7: null,
    image_7_name: "",
    image_8: null,
    image_8_name: "",
    image_9: null,
    image_9_name: "",
    image_10: null,
    image_10_name: "",
    image_11: null,
    image_11_name: "",
    image_12: null,
    image_12_name: "",
    image_13: null,
    image_13_name: "",
    image_14: null,
    image_14_name: "",
    // MTVH additional photo fields (12 photos total: 4 main + 8 additional a/b variants)
    image_1_a: null,
    image_1_b: null,
    image_2_a: null,
    image_2_b: null,
    image_3_a: null,
    image_3_b: null,
    image_4_a: null,
    image_4_b: null,
    
    // Checklist sections (will be converted to JSON)
    external: {},
    kitchen: {},
    living_space: {},
    bathroom: {},
    other: {},
    health_and_safety: {},
    location: {},
    
    // Signature
    e_sign: null,
  });

  // Locations state for dynamic locations - default includes "Communal area - Location" (matches Laravel)
  const [locations, setLocations] = useState(["Communal area - Location"]);
  const [newLocation, setNewLocation] = useState("");

  // Dynamic meters
  const [newMeterType, setNewMeterType] = useState("");
  // Which meter photo slots are currently shown (subset of METER_PHOTO_POOL,
  // in order). Defaults to the standard four; extended on load if a report
  // already has data in image_9..image_14, and via the "Add photo" button.
  const [meterSlots, setMeterSlots] = useState(DEFAULT_METER_SLOTS);

  // The property's current meter register (Oct 2026+ reports).
  const [propertyMeters, setPropertyMeters] = useState([]);
  // Meters already recorded on THIS report, which may include ones since
  // removed from the register. Kept so editing an old report neither hides
  // nor deletes a reading that was genuinely taken that month.
  const [reportMeters, setReportMeters] = useState([]);
  // Distinguishes 'no meters' from 'not fetched yet'.
  const [metersLoaded, setMetersLoaded] = useState(false);
  // The property is marked as genuinely having no meters.
  const [noMetersConfirmed, setNoMetersConfirmed] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await propertyMetersService.getMetersByProperty(
          propertyId
        );
        if (!cancelled && response.data?.success) {
          setPropertyMeters(response.data.data || []);
          setNoMetersConfirmed(Boolean(response.data.no_meters_confirmed));
        }
      } catch (error) {
        console.error('Error loading property meters:', error);
      } finally {
        if (!cancelled) setMetersLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  // Months data
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load property data
        const propertyResponse = await propertyMonthlyInspectionsService.getFormData(propertyId);
        if (propertyResponse.data.data) {
          setProperty(propertyResponse.data.data.property);

          // Check if this is a Metropolitan Housing (MTVH) property
          const isMTVH = propertyResponse.data.data.isMetropolitanHousing || false;
          setIsMetropolitanHousing(isMTVH);

          // Store ticket types for MTVH checklist
          if (propertyResponse.data.data.ticketTypes) {
            setTicketTypes(propertyResponse.data.data.ticketTypes);
          }

          // Store monthly inspection responses for MTVH notes dropdown
          if (propertyResponse.data.data.monthlyInspectionResponses) {
            setMonthlyInspectionResponses(propertyResponse.data.data.monthlyInspectionResponses);
          }
        } else if (propertyResponse.data.property) {
          setProperty(propertyResponse.data.property);
        }

        // Load existing report data
        const reportResponse = await propertyMonthlyInspectionsService.getMonthlyInspectionReport(reportId);
        console.log('🔍 [DEBUG] Report API response:', reportResponse);
        if (reportResponse.data && reportResponse.data.data) {
          console.log('🔍 [DEBUG] Report data received:', reportResponse.data.data);
          console.log('🔍 [DEBUG] Signature field (e_sign):', reportResponse.data.data.e_sign);
          console.log('🔍 [DEBUG] Inspection date field (raw):', reportResponse.data.data.inspection_date);
          console.log('🔍 [DEBUG] Lease start date field (raw):', reportResponse.data.data.lease_start_date);
          console.log('🔍 [DEBUG] Reading fields:', reportResponse.data.data.reading_fields);
          console.log('🔍 [DEBUG] Photo fields:', {
            image_1: reportResponse.data.data.image_1,
            image_1_name: reportResponse.data.data.image_1_name,
            image_2: reportResponse.data.data.image_2,
            image_2_name: reportResponse.data.data.image_2_name
          });
          setReport(reportResponse.data.data);
          
          // Parse JSON fields from database (backend already parses them, but handle both cases)
          const reportData = reportResponse.data.data;

          // Meters recorded on this report, shaped like register entries.
          // Includes any since removed from the property, which the register
          // fetch will not return.
          if (Array.isArray(reportData.meter_readings)) {
            setReportMeters(
              reportData.meter_readings
                .filter((row) => row.property_meter_id)
                .map((row) => ({
                  id: row.property_meter_id,
                  meter_type: row.meter_type,
                  serial_number: row.serial_number,
                  location: row.location,
                }))
            );
          }
          const parsedData = {
            ...reportData,
            property_id: parseInt(propertyId),
            // Convert inspection_date from ISO format to d/m/Y format (Laravel format for validation)
            inspection_date: reportData.inspection_date ? 
              (() => {
                try {
                  console.log('🔍 [DATE PARSING] Raw inspection_date from backend:', reportData.inspection_date, typeof reportData.inspection_date);
                  // If already in d/m/Y format, return as is
                  if (typeof reportData.inspection_date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(reportData.inspection_date)) {
                    console.log('🔍 [DATE PARSING] Already in d/m/Y format:', reportData.inspection_date);
                    return reportData.inspection_date;
                  }
                  const date = new Date(reportData.inspection_date);
                  if (!isNaN(date.getTime())) {
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    const formatted = `${day}/${month}/${year}`;
                    console.log('🔍 [DATE PARSING] Converted inspection_date to d/m/Y:', reportData.inspection_date, '->', formatted);
                    return formatted;
                  } else {
                    console.warn('🔍 [DATE PARSING] Invalid date for inspection_date:', reportData.inspection_date);
                  }
                } catch (e) {
                  console.error('❌ [DATE PARSING] Error parsing inspection_date:', e, reportData.inspection_date);
                }
                return reportData.inspection_date || "";
              })() : 
              "",
            // Convert lease_start_date from ISO format to d/m/Y format (Laravel format for validation)
            lease_start_date: reportData.lease_start_date ? 
              (() => {
                try {
                  console.log('🔍 [DATE PARSING] Raw lease_start_date from backend:', reportData.lease_start_date, typeof reportData.lease_start_date);
                  // If already in d/m/Y format, return as is
                  if (typeof reportData.lease_start_date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(reportData.lease_start_date)) {
                    console.log('🔍 [DATE PARSING] Already in d/m/Y format:', reportData.lease_start_date);
                    return reportData.lease_start_date;
                  }
                  const date = new Date(reportData.lease_start_date);
                  if (!isNaN(date.getTime())) {
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    const formatted = `${day}/${month}/${year}`;
                    console.log('🔍 [DATE PARSING] Converted lease_start_date to d/m/Y:', reportData.lease_start_date, '->', formatted);
                    return formatted;
                  } else {
                    console.warn('🔍 [DATE PARSING] Invalid date for lease_start_date:', reportData.lease_start_date);
                  }
                } catch (e) {
                  console.error('❌ [DATE PARSING] Error parsing lease_start_date:', e, reportData.lease_start_date);
                }
                return reportData.lease_start_date || "";
              })() : 
              "",
            // Handle signature field explicitly
            e_sign: reportData.e_sign || null,
            // Handle reading fields - extract from combined structure
            ...(() => {
              let readingFields = [];
              let readingFieldsNames = [];
              let readingFieldsSerials = [];
              
              if (reportData.reading_fields) {
                const parsed = Array.isArray(reportData.reading_fields) 
                  ? reportData.reading_fields 
                  : JSON.parse(reportData.reading_fields);
                
                console.log('🔍 [DEBUG] Parsed reading_fields:', parsed);
                console.log('🔍 [DEBUG] Parsed length:', parsed.length);
                console.log('🔍 [DEBUG] First item:', parsed[0]);
                console.log('🔍 [DEBUG] Is first item array?', Array.isArray(parsed[0]));
                
                if (Array.isArray(parsed)) {
                  // Check if it's Laravel's structure: [[name, reading, serial], ...]
                  if (parsed.length > 0 && Array.isArray(parsed[0])) {
                    // Laravel structure: [[name, reading, serial], ...]
                    console.log('🔍 [DEBUG] Processing Laravel structure with', parsed.length, 'items');
                    readingFieldsNames = parsed.map(item => item[0] || '');
                    readingFields = parsed.map(item => item[1] || '');
                    readingFieldsSerials = parsed.map(item => item[2] || '');
                    console.log('🔍 [DEBUG] Using Laravel structure for meter data:', {
                      readingFields,
                      readingFieldsNames,
                      readingFieldsSerials
                    });
                  } else if (parsed.length > 0 && parsed[0].reading !== undefined) {
                    // Our new structure: [{reading, name, serial}, ...]
                    readingFields = parsed.map(item => item.reading || '');
                    readingFieldsNames = parsed.map(item => item.name || '');
                    readingFieldsSerials = parsed.map(item => item.serial || '');
                    console.log('🔍 [DEBUG] Using new structure for meter data:', {
                      readingFields,
                      readingFieldsNames,
                      readingFieldsSerials
                    });
                  } else {
                    // Old structure: just array of readings
                    readingFields = parsed;
                    readingFieldsNames = parsed.map((_, index) => `Reading ${index + 1}`);
                    readingFieldsSerials = parsed.map(() => '');
                    console.log('🔍 [DEBUG] Using old structure for meter data:', {
                      readingFields,
                      readingFieldsNames,
                      readingFieldsSerials
                    });
                  }
                }
              }
              
              return {
                reading_fields: readingFields,
                reading_fields_names: readingFieldsNames,
                reading_fields_serials: readingFieldsSerials
              };
            })(),
            // Oct 2026+ meters. The API returns rows joined to the register;
            // the form submits readings keyed by meter id, so convert here.
            // Photo key/url are stored under the same `meter_photo_<id>`
            // field names the upload widget uses.
            ...(() => {
              const rows = Array.isArray(reportData.meter_readings)
                ? reportData.meter_readings
                : [];
              const readings = {};
              const photos = {};
              rows.forEach((row) => {
                readings[row.property_meter_id] = row.reading || '';
                if (row.photo) {
                  photos['meter_photo_' + row.property_meter_id] = row.photo;
                }
                if (row.photo_url) {
                  photos['meter_photo_' + row.property_meter_id + '_url'] =
                    row.photo_url;
                }
              });
              return { meter_readings: readings, ...photos };
            })(),
            external: typeof reportData.external === 'object' ? reportData.external : 
                     (reportData.external ? JSON.parse(reportData.external) : {}),
            kitchen: typeof reportData.kitchen === 'object' ? reportData.kitchen : 
                    (reportData.kitchen ? JSON.parse(reportData.kitchen) : {}),
            living_space: typeof reportData.living_space === 'object' ? reportData.living_space : 
                         (reportData.living_space ? JSON.parse(reportData.living_space) : {}),
            bathroom: typeof reportData.bathroom === 'object' ? reportData.bathroom : 
                     (reportData.bathroom ? JSON.parse(reportData.bathroom) : {}),
            other: typeof reportData.other === 'object' ? reportData.other : 
                  (reportData.other ? JSON.parse(reportData.other) : {}),
            health_and_safety: typeof reportData.health_and_safety === 'object' ? reportData.health_and_safety : 
                              (reportData.health_and_safety ? JSON.parse(reportData.health_and_safety) : {}),
            location: typeof reportData.location === 'object' ? reportData.location : 
                     (reportData.location ? JSON.parse(reportData.location) : {}),
          };
          
          // Store presigned URLs separately for PhotoUpload components
          const imageUrls = {
            image_1_url: reportData.image_1_url || null,
            image_2_url: reportData.image_2_url || null,
            image_3_url: reportData.image_3_url || null,
            image_4_url: reportData.image_4_url || null,
            image_5_url: reportData.image_5_url || null,
            image_6_url: reportData.image_6_url || null,
            image_7_url: reportData.image_7_url || null,
            image_8_url: reportData.image_8_url || null,
            image_9_url: reportData.image_9_url || null,
            image_10_url: reportData.image_10_url || null,
            image_11_url: reportData.image_11_url || null,
            image_12_url: reportData.image_12_url || null,
            image_13_url: reportData.image_13_url || null,
            image_14_url: reportData.image_14_url || null,
          };

          // Show any extra meter slots (image_9..image_14) that already hold
          // data, on top of the standard four.
          const activeMeterSlots = METER_PHOTO_POOL.filter(
            (f, i) =>
              i < DEFAULT_METER_SLOTS.length ||
              reportData[f] ||
              reportData[`${f}_name`] ||
              reportData[`${f}_url`]
          );
          setMeterSlots(
            activeMeterSlots.length ? activeMeterSlots : DEFAULT_METER_SLOTS
          );

          // Populate form with existing data, ensuring required fields are preserved
          setFormData(prevData => {
            const newFormData = {
              ...prevData,
              ...parsedData,
              // Store presigned URLs in form data for PhotoUpload components
              ...imageUrls,
              // Ensure required fields are always set
              type_id: parsedData.type_id || 1,
              property_id: parseInt(propertyId) || 0,
              month: parsedData.month || 1,
              year: parsedData.year || new Date().getFullYear(),
              status_id: parsedData.status_id !== undefined ? parsedData.status_id : 0,
            };
            console.log('🔍 [DEBUG] Setting form data:', newFormData);
            console.log('🔍 [DEBUG] Signature in form data (e_sign):', newFormData.e_sign);
            console.log('🔍 [DEBUG] Inspection date in form data:', newFormData.inspection_date);
            console.log('🔍 [DEBUG] Lease start date in form data:', newFormData.lease_start_date);
            console.log('🔍 [DEBUG] Image URLs from backend:', imageUrls);
            return newFormData;
          });
          
          // Extract locations from the parsed location data
          // The location field contains nested objects like { "Communal area - Location": {...}, "Communal area - Kitchen": {...} }
          const parsedLocation = typeof reportData.location === 'object' ? reportData.location :
                                (reportData.location ? JSON.parse(reportData.location) : {});
          const locationKeys = Object.keys(parsedLocation);
          const defaultLocation = "Communal area - Location";

          if (locationKeys.length > 0) {
            // Ensure default location is first, then add any other locations
            if (!locationKeys.includes(defaultLocation)) {
              setLocations([defaultLocation, ...locationKeys]);
            } else {
              // Put default location first, then others
              const otherLocations = locationKeys.filter(loc => loc !== defaultLocation);
              setLocations([defaultLocation, ...otherLocations]);
            }
            console.log('🔍 [DEBUG] Loaded locations from report data:', locationKeys);
          }
          // If no locations in data, keep the default (already set in initial state)
        }
      } catch (error) {
        console.error('❌ [FRONTEND] Error loading data:', error);
        toast.error('Failed to load report data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [propertyId, reportId]);

  // Date picker refs
  const inspectionDateInputRef = useRef(null);
  const leaseStartDateInputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Offline upload support (edit mode — submits via PUT, keyed by reportId)
  // ---------------------------------------------------------------------------
  const genUuid = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });

  const draftRef = useRef({ id: null, clientUuid: null, photos: {} });
  const draftActiveRef = useRef(false);
  const objectUrlsRef = useRef({});

  const formDataRef = useRef(formData);
  const photoRotationsRef = useRef(photoRotations);
  const locationsRef = useRef(locations);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  useEffect(() => {
    photoRotationsRef.current = photoRotations;
  }, [photoRotations]);

  const ensureDraftId = () => {
    if (!draftRef.current.id) {
      // Deterministic per report so re-entering the edit resumes the same draft.
      draftRef.current.id = `mir-edit-${reportId}`;
      draftRef.current.clientUuid = genUuid();
      if (!draftRef.current.photos) draftRef.current.photos = {};
    }
  };

  // Snapshot the form. Strip transient presigned `*_url` keys (they expire), and
  // force flat photo slots (records with a `field`) to match buffered state.
  const snapshotFormData = () => {
    const fd = {};
    Object.keys(formDataRef.current).forEach((k) => {
      if (!k.endsWith("_url")) fd[k] = formDataRef.current[k];
    });
    const files = draftRef.current.photos || {};
    Object.keys(files).forEach((key) => {
      const rec = files[key];
      if (rec && rec.field) {
        fd[rec.field] = rec.s3Key ? rec.s3Key : OFFLINE_PHOTO_PREFIX + key;
      }
    });
    return fd;
  };

  const persistDraft = async (status) => {
    ensureDraftId();
    const record = {
      id: draftRef.current.id,
      propertyId: String(propertyId),
      reportId: String(reportId),
      mode: "update",
      clientUuid: draftRef.current.clientUuid,
      status,
      formData: snapshotFormData(),
      photoRotations: photoRotationsRef.current || {},
      locations: locationsRef.current || [],
      photos: draftRef.current.photos || {},
      attempts: 0,
      permanent: false,
      lastError: null,
    };
    await saveDraft(record);
    draftActiveRef.current = true;
  };

  // Buffer a file selected while offline (photos: opts.field; checklist: no field).
  const handleOfflineSave = useCallback(
    async (file, opts = {}) => {
      ensureDraftId();
      const { field, folder } = opts;
      const key = field || `cf_${genUuid()}`;
      const prev = draftRef.current.photos[key];
      draftRef.current.photos[key] = {
        blob: file,
        fileName: file.name,
        mimeType: file.type,
        folder:
          folder || `uploads/images/properties/${propertyId}/reports/mir/`,
        field: field || null,
      };
      try {
        await persistDraft("draft");
      } catch (err) {
        if (prev) draftRef.current.photos[key] = prev;
        else delete draftRef.current.photos[key];
        throw err;
      }
      return OFFLINE_PHOTO_PREFIX + key;
    },
    [propertyId, reportId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const queueForSync = async () => {
    await persistDraft("queued");
    toast.info(
      "Saved. Your changes will upload automatically when you’re back online."
    );
    triggerSync();
    navigate(`/properties/${propertyId}`);
  };

  const restoreDraft = async () => {
    try {
      const saved = await getResumableDraftByReport(reportId);
      if (!saved) return;
      draftRef.current = {
        id: saved.id,
        clientUuid: saved.clientUuid,
        photos: saved.photos || {},
      };
      draftActiveRef.current = true;
      if (saved.photoRotations) setPhotoRotations(saved.photoRotations);
      if (saved.locations && saved.locations.length)
        setLocations(saved.locations);

      // For each offline-changed photo, point its `*_url` at the buffered Blob so
      // the thumbnail shows the NEW image (not the stale presigned URL of the
      // photo it replaced). Unchanged photos keep their loaded presigned URLs.
      const urlOverrides = {};
      const previewUrls = {};
      Object.values(saved.photos || {}).forEach((rec) => {
        if (rec && rec.blob && rec.field) {
          const url = URL.createObjectURL(rec.blob);
          objectUrlsRef.current[rec.field] = url;
          urlOverrides[`${rec.field}_url`] = url;
          previewUrls[rec.field] = url;
        }
      });
      if (saved.formData) {
        setFormData((prev) => ({ ...prev, ...saved.formData, ...urlOverrides }));
      }
      if (Object.keys(previewUrls).length) {
        setPhotoPreviewUrls((p) => ({ ...p, ...previewUrls }));
      }
      toast.info("Resumed your saved offline edits.");
    } catch (e) {
      console.warn("[MIR edit] restore draft failed:", e && e.message);
    }
  };

  useEffect(() => {
    if (!draftActiveRef.current) return undefined;
    const t = setTimeout(() => {
      persistDraft("draft").catch(() => {});
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, photoRotations, locations]);

  useEffect(
    () => () => {
      Object.values(objectUrlsRef.current).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {
          /* no-op */
        }
      });
    },
    []
  );

  // Resume a saved offline edit draft once the report has finished loading.
  useEffect(() => {
    if (!loading) restoreDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Convert d/m/Y format to YYYY-MM-DD (for date input)
  const convertToDateInputFormat = useCallback((dateString) => {
    if (!dateString) return "";
    console.log('🔍 [DATE CONVERSION] Input dateString:', dateString);
    
    // If already in YYYY-MM-DD format, return as is
    if (dateString.includes("-") && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      console.log('🔍 [DATE CONVERSION] Already in YYYY-MM-DD format:', dateString);
      return dateString;
    }
    
    // Handle d/m/Y format (Laravel format)
    if (dateString.includes("/")) {
      const parts = dateString.split("/");
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const formatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        console.log('🔍 [DATE CONVERSION] Converted d/m/Y to YYYY-MM-DD:', dateString, '->', formatted);
        return formatted;
      }
    }
    
    // Try to parse as Date object if it's an ISO string
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formatted = `${year}-${month}-${day}`;
        console.log('🔍 [DATE CONVERSION] Converted Date object to YYYY-MM-DD:', dateString, '->', formatted);
        return formatted;
      }
    } catch (e) {
      console.error('🔍 [DATE CONVERSION] Error parsing date:', e);
    }
    
    console.warn('🔍 [DATE CONVERSION] Could not convert date:', dateString);
    return "";
  }, []);

  // Handle date change from native date picker (YYYY-MM-DD format)
  const handleDateChange = useCallback((field, e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      // Convert from YYYY-MM-DD to d/m/Y format (Laravel format)
      const [year, month, day] = dateValue.split("-");
      const formattedDate = `${day}/${month}/${year}`;
      setFormData((prev) => ({
        ...prev,
        [field]: formattedDate,
      }));
    }
  }, []);

  // Open native date picker
  const openDatePicker = useCallback((fieldName, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log('🔍 [DATE PICKER] Opening date picker for:', fieldName);
    let dateInputRef;
    if (fieldName === "inspection_date") {
      dateInputRef = inspectionDateInputRef;
    } else if (fieldName === "lease_start_date") {
      dateInputRef = leaseStartDateInputRef;
    }

    if (dateInputRef?.current) {
      console.log('🔍 [DATE PICKER] Date input ref found, current value:', dateInputRef.current.value);
      setTimeout(() => {
        try {
          if (dateInputRef.current.showPicker) {
            console.log('🔍 [DATE PICKER] Using showPicker() method');
            dateInputRef.current.showPicker();
          } else {
            console.log('🔍 [DATE PICKER] showPicker() not available, using click()');
            dateInputRef.current.click();
          }
        } catch (error) {
          console.error('❌ [DATE PICKER] Error opening picker:', error);
          try {
            dateInputRef.current.click();
          } catch (clickError) {
            console.error('❌ [DATE PICKER] Error with click() fallback:', clickError);
          }
        }
      }, 10);
    } else {
      console.error('❌ [DATE PICKER] Date input ref not found for:', fieldName);
    }
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle photo upload
  const handlePhotoUpload = (fieldName, file, fileName) => {
    // Removal: also drop any buffered offline Blob + restored preview URL.
    if (file === null) {
      if (draftRef.current.photos && draftRef.current.photos[fieldName]) {
        delete draftRef.current.photos[fieldName];
      }
      if (objectUrlsRef.current[fieldName]) {
        try {
          URL.revokeObjectURL(objectUrlsRef.current[fieldName]);
        } catch (_) {
          /* no-op */
        }
        delete objectUrlsRef.current[fieldName];
      }
      setPhotoPreviewUrls((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    }
    setFormData(prev => ({
      ...prev,
      [fieldName]: file,
      [`${fieldName}_name`]: fileName,
      [`${fieldName}_url`]: file === null ? null : prev[`${fieldName}_url`],
    }));
  };

  // Handle checklist changes - supports nested location structure
  const handleChecklistChange = (section, item, field, value) => {
    // Handle location items which have nested structure: location[locationName][itemName]
    if (section === 'location' && item.includes('.')) {
      const [locationName, itemName] = item.split('.');
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          [locationName]: {
            ...prev.location?.[locationName],
            [itemName]: {
              ...prev.location?.[locationName]?.[itemName],
              [field]: value
            }
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [item]: {
            ...prev[section]?.[item],
            [field]: value
          }
        }
      }));
    }
  };

  // Handle location management - format as "Communal area - {name}" (matches Laravel)
  const handleAddLocation = () => {
    if (newLocation.trim()) {
      const formattedLocation = `Communal area - ${newLocation.trim()}`;
      if (!locations.includes(formattedLocation)) {
        setLocations(prev => [...prev, formattedLocation]);
        setNewLocation("");
      }
    }
  };

  const handleRemoveLocation = (locationToRemove) => {
    setLocations(prev => prev.filter(location => location !== locationToRemove));
    // Remove location data from formData
    setFormData(prev => {
      const newData = { ...prev };
      if (newData.location && newData.location[locationToRemove]) {
        delete newData.location[locationToRemove];
      }
      return newData;
    });
  };

  // Handle meter addition
  const handleAddMeter = () => {
    if (newMeterType.trim()) {
      setFormData((prev) => ({
        ...prev,
        reading_fields: [...prev.reading_fields, ""],
        reading_fields_names: [
          ...prev.reading_fields_names,
          newMeterType.trim(),
        ],
        reading_fields_serials: [...prev.reading_fields_serials, ""],
      }));
      setNewMeterType("");
    }
  };

  // Handle meter removal
  const handleRemoveMeter = (index) => {
    setFormData((prev) => ({
      ...prev,
      reading_fields: prev.reading_fields.filter((_, i) => i !== index),
      reading_fields_names: prev.reading_fields_names.filter(
        (_, i) => i !== index
      ),
      reading_fields_serials: prev.reading_fields_serials.filter(
        (_, i) => i !== index
      ),
    }));
  };

  // Add the next available meter photo slot (up to the pool of ten). Seeds a
  // default name so the required Name field isn't left blank.
  const handleAddMeterPhoto = () => {
    const next = METER_PHOTO_POOL.find((f) => !meterSlots.includes(f));
    if (!next) return; // pool exhausted (10 slots)
    setMeterSlots((prev) => (prev.includes(next) ? prev : [...prev, next]));
    setFormData((prev) => ({
      ...prev,
      [`${next}_name`]:
        prev[`${next}_name`] ||
        METER_DEFAULT_NAMES[next] ||
        `Meter ${meterSlots.length + 1}`,
    }));
  };

  // Remove a meter photo slot: hide it and clear its image + name locally.
  // handlePhotoUpload(field, null, "") also drops any buffered offline blob /
  // preview for the slot.
  //
  // The report update endpoint deliberately ignores empty values (it never
  // overwrites saved data with blanks), so a Save alone would NOT clear a
  // previously-saved photo/name — the slot would reappear on reload. We
  // therefore null the image and its name column directly via the dedicated
  // removeImage endpoint. It's idempotent, so it's safe to call for a slot that
  // was only added this session (nothing persisted yet).
  const handleRemoveMeterPhoto = async (field) => {
    setMeterSlots((prev) => prev.filter((f) => f !== field));
    handlePhotoUpload(field, null, "");

    if (!reportId) return;
    try {
      await propertyMonthlyInspectionsService.removeImage(reportId, field);
      await propertyMonthlyInspectionsService.removeImage(
        reportId,
        `${field}_name`
      );
    } catch (error) {
      console.error(
        `❌ [FRONTEND] Failed to clear meter photo ${field} on server:`,
        error
      );
      toast.error(
        "Could not remove that meter photo on the server — it may reappear on reload."
      );
    }
  };

  // Handle meter field change
  // Oct 2026+: one reading per registered meter, keyed by meter id.
  const handleMeterReadingChange = (meterId, value) => {
    setFormData((prev) => ({
      ...prev,
      meter_readings: { ...(prev.meter_readings || {}), [meterId]: value },
    }));
  };

  const handleMeterChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  // Handle signature
  const handleSignature = (signatureData) => {
    setFormData(prev => ({
      ...prev,
      e_sign: signatureData
    }));
  };


  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Explicit signature validation for non-MTVH properties (matching Laravel behavior)
    // This is a critical validation that must happen before other validations
    if (!isMetropolitanHousing) {
      if (!formData.e_sign || formData.e_sign === '' || formData.e_sign === 'null' ||
          (typeof formData.e_sign === 'string' && formData.e_sign.trim() === '')) {
        alert('You have to sign! The field is blank!');
        return;
      }
    }

    // Validate form data before submission (matching Laravel behavior)
    // Pass isMetropolitanHousing flag to skip signature validation for MTVH properties
    if (
      !validateAndShowErrors(formData, {
        isMetropolitanHousing,
        meters: useNewMeterRegister ? editableMeters : [],
        requiredMeterIds: useNewMeterRegister ? requiredMeterIds : null,
        noMetersConfirmed,
      })
    ) {
      return; // Stop submission if validation fails
    }

    const hasBufferedFiles =
      Object.keys(draftRef.current.photos || {}).length > 0;
    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;

    setSubmitting(true);

    // Offline, or files still buffered locally: queue the edit for background
    // sync (PUT replayed when online; naturally idempotent).
    if (!isOnline || hasBufferedFiles) {
      try {
        await queueForSync();
      } catch (err) {
        setSubmitting(false);
        toast.error(
          "Could not save these changes for offline upload: " +
            (err?.message || "storage unavailable")
        );
      }
      return;
    }

    try {
      // Merge any pending rotations into the submit payload as image_X_rotate.
      // Backend rotates the S3 file in place and strips these fields before DB save.
      const submitData = { ...formData };
      Object.entries(photoRotations).forEach(([field, degrees]) => {
        if (degrees) submitData[`${field}_rotate`] = degrees;
      });

      await propertyMonthlyInspectionsService.updateMonthlyInspectionReport(reportId, submitData);
      // Online update succeeded — clear any stale draft for this report.
      if (draftActiveRef.current) {
        await deleteDraft(draftRef.current.id).catch(() => {});
        draftActiveRef.current = false;
      }
      // S3 file is now physically rotated — drop pending rotation state so
      // returning to the form doesn't double-apply the rotation.
      setPhotoRotations({});
      toast.success('Monthly Inspection Report updated successfully!');
      navigate(`/properties/${propertyId}`);
    } catch (error) {
      console.error('❌ [FRONTEND] Error updating report:', error);
      // Network failure (no server response) → fall back to the offline queue.
      if (!error?.response) {
        try {
          await queueForSync();
          return;
        } catch (_) {
          /* fall through to error toast */
        }
      }
      toast.error('Failed to update report');
      setSubmitting(false);
    }
  };

  // Photo slot ordering — left-to-right, top-to-bottom across the 4 named columns.
  // Standard layout = 4 single slots; MTVH = 4 columns × 3 rows (main + a + b).
  const inspectionPhotoFields = isMetropolitanHousing
    ? [
        'image_1', 'image_1_a', 'image_1_b',
        'image_2', 'image_2_a', 'image_2_b',
        'image_3', 'image_3_a', 'image_3_b',
        'image_4', 'image_4_a', 'image_4_b',
      ]
    : ['image_1', 'image_2', 'image_3', 'image_4'];

  // Resolve a slot's image URL. Priority:
  //   1. Local data-URL preview (just-uploaded, before reload) — same image
  //      the thumbnail is showing right now, no network round-trip.
  //   2. Backend-provided presigned URL (image_X_url) — only set on report load.
  //   3. Backend S3 proxy (/api/s3/file/<key>) — generic fallback when only
  //      the S3 key is in state.
  const resolveSlotUrl = (field) => {
    const preview = photoPreviewUrls[field];
    if (preview) return preview;
    const url = formData[`${field}_url`];
    if (url) return url;
    const key = formData[field];
    if (!key || typeof key !== 'string') return null;
    if (key.startsWith('http')) return key;
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    return `${apiBase}/api/s3/file/${key}`;
  };

  // Build slides from any populated slots — empty slots are excluded so prev/next
  // moves through only the photos that actually exist.
  const inspectionGallerySlides = inspectionPhotoFields
    .map((field) => {
      const src = resolveSlotUrl(field);
      if (!src) return null;
      const baseField = field.replace(/_[ab]$/, '');
      const categoryName = formData[`${baseField}_name`] || '';
      const variantSuffix = field.endsWith('_a') ? ' (2)' : field.endsWith('_b') ? ' (3)' : '';
      return {
        field,
        src,
        title: `${categoryName}${variantSuffix}`,
      };
    })
    .filter(Boolean);

  const openInspectionGallery = (field) => {
    const idx = inspectionGallerySlides.findIndex((s) => s.field === field);
    if (idx >= 0) {
      setGalleryIndex(idx);
      setGalleryOpen(true);
    }
  };

  // slide-index → rotation map (the lightbox addresses slides by index, the
  // form state addresses photos by field name).
  const inspectionGalleryRotations = inspectionGallerySlides.reduce((acc, slide, i) => {
    const r = photoRotations[slide.field];
    if (r) acc[i] = r;
    return acc;
  }, {});

  const handleInspectionGalleryRotate = (slideIdx) => {
    const slide = inspectionGallerySlides[slideIdx];
    if (!slide) return;
    rotatePhotoField(slide.field);
  };

  // Meters photo grid (image_5..image_14). Shown for every client, MTVH
  // included. Mirrors the currently-shown slots.
  // Which meter scheme this report uses, decided by its reporting period.
  const useNewMeterRegister = usesNewMeters(formData);

  // Meters to show: the current register PLUS any already recorded on this
  // report. Without the union, a meter removed from the property since this
  // report was filed would disappear from the form -- and saving would then
  // delete a reading that was genuinely taken.
  const editableMeters = useNewMeterRegister
    ? (() => {
        const seen = new Set();
        const combined = [];
        [...propertyMeters, ...reportMeters].forEach((meter) => {
          if (!meter || seen.has(meter.id)) return;
          seen.add(meter.id);
          combined.push(meter);
        });
        return combined;
      })()
    : [];

  const meterPhotoLabels = useNewMeterRegister
    ? editableMeters.reduce((acc, meter) => {
        acc['meter_photo_' + meter.id] =
          meter.meter_type + ' \u2014 ' + meter.serial_number;
        return acc;
      }, {})
    : {};

  // True once we know there are no meters to read AND the report's period
  // needs the register. Uses the union list, so a report still carrying
  // readings for a since-removed meter is not blocked.
  const reportMeterIds = reportMeters.map((meter) => meter.id);
  const requiredMeterIds =
    reportMeterIds.length > 0
      ? reportMeterIds
      : editableMeters.map((meter) => meter.id);

  const meterRegisterMissing =
    metersLoaded &&
    useNewMeterRegister &&
    editableMeters.length === 0 &&
    !noMetersConfirmed;

  useEffect(() => {
    if (meterRegisterMissing) {
      toast.error(
        'No meters are set up for this property. Add them on the property ' +
          'page before saving this inspection.'
      );
    }
  }, [meterRegisterMissing]);

  const meterPhotoFields = useNewMeterRegister
    ? editableMeters.map((meter) => 'meter_photo_' + meter.id)
    : meterSlots;
  const meterGallerySlides = meterPhotoFields
    .map((field) => {
      const src = resolveSlotUrl(field);
      if (!src) return null;
      return {
        field,
        src,
        title: meterPhotoLabels[field] || formData[`${field}_name`] || '',
      };
    })
    .filter(Boolean);

  const openMeterGallery = (field) => {
    const idx = meterGallerySlides.findIndex((s) => s.field === field);
    if (idx >= 0) {
      setMeterGalleryIndex(idx);
      setMeterGalleryOpen(true);
    }
  };

  const meterGalleryRotations = meterGallerySlides.reduce((acc, slide, i) => {
    const r = photoRotations[slide.field];
    if (r) acc[i] = r;
    return acc;
  }, {});

  const handleMeterGalleryRotate = (slideIdx) => {
    const slide = meterGallerySlides[slideIdx];
    if (!slide) return;
    rotatePhotoField(slide.field);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} container`}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/" className={styles.breadcrumbLink}>Dashboard</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to="/properties" className={styles.breadcrumbLink}>Properties</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to={`/properties/${propertyId}`} className={styles.breadcrumbLink}>{property?.name || 'Property'}</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>Edit Monthly Inspection Report</span>
      </div>

      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1>Edit Monthly Inspection Report</h1>
        <p>Property: {property?.name || 'Loading...'}</p>
      </div>

      {/* Form */}
      {meterRegisterMissing && (
        <div
          style={{
            margin: '0 0 20px',
            padding: '16px',
            border: '1px solid #ebccd1',
            borderRadius: '4px',
            background: '#f2dede',
            color: '#a94442',
          }}
        >
          <strong>
            This report cannot be saved: no meters are set up for this
            property.
          </strong>
          <div style={{ marginTop: '6px' }}>
            From October 2026, readings are taken against the property&rsquo;s
            meter register. Add each meter &mdash; type, serial number and
            location &mdash; on the property page, then return here.
          </div>
          <div style={{ marginTop: '10px' }}>
            <Link
              to={`/properties/${propertyId}`}
              className="btn btn-primary"
              target="_blank"
            >
              Open property page
            </Link>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Report Section */}
        <div className="row">
          <div className="col-xs-12">
            <div className="panel panel-default">
              <div className="panel-heading">
                <h4 className="panel-title">Report</h4>
              </div>
              <div className="panel-body">
                <div className="row">
                  <div className="col-xs-12">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                        onClick={async () => {
                          try {
                            const reportUrl = `/api/monthlyinspectionreports/${reportId}/download-pdf`;
                            const response = await fetch(reportUrl, {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              }
                            });
                            if (!response.ok) {
                              throw new Error(`HTTP error! status: ${response.status}`);
                            }
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `monthly-inspection-report-${reportId}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            toast.success("Report downloaded successfully!");
                          } catch (error) {
                            console.error("Error downloading report:", error);
                            toast.error("Failed to download report. Please try again.");
                          }
                        }}
                      >
                        <i className="fa fa-file-pdf-o"></i> Report
                        <span style={{ fontSize: "0.8em", marginLeft: "10px" }}>Updated: {new Date().toLocaleDateString('en-GB')}</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                      <label style={{ margin: 0, whiteSpace: "nowrap", fontWeight: "bold" }}>
                        Status <span style={{ color: "red" }}>*</span>
                      </label>
                      <select name="status_id" value={formData.status_id} onChange={handleInputChange} className="form-control">
                        <option value={0}>Unpublished</option>
                        <option value={1}>Published</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Photos Section - Different layout for MTVH vs Standard */}
        <div className={styles.reportSectionHeader}>Photos</div>
        {isMetropolitanHousing ? (
          <>
            {/* MTVH: 4 columns, each with Name field at top and 3 photos stacked below (matching Laravel layout) */}
            <div className={styles.row}>
              {/* Column 1: Front */}
              <div className={styles.colMd3}>
                <div className={styles.formGroup}>
                  <label className={styles.controlLabel}>Name <span className={styles.required}>*</span></label>
                  <div className={styles.colSm10}>
                    <input type="text" name="image_1_name" value={formData.image_1_name || "Front"} onChange={handleInputChange} className={styles.formControl} />
                  </div>
                </div>
                <PhotoUpload label="Front" fieldName="image_1" onUpload={handlePhotoUpload} existingImage={formData.image_1} existingImageName={formData.image_1_name} existingImageUrl={formData.image_1_url} onPreviewClick={() => openInspectionGallery('image_1')} rotation={photoRotations.image_1 || 0} onRotate={() => rotatePhotoField('image_1')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
                <PhotoUpload label="Front" fieldName="image_1_a" onUpload={handlePhotoUpload} existingImage={formData.image_1_a} existingImageUrl={formData.image_1_a_url} onPreviewClick={() => openInspectionGallery('image_1_a')} rotation={photoRotations.image_1_a || 0} onRotate={() => rotatePhotoField('image_1_a')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
                <PhotoUpload label="Front" fieldName="image_1_b" onUpload={handlePhotoUpload} existingImage={formData.image_1_b} existingImageUrl={formData.image_1_b_url} onPreviewClick={() => openInspectionGallery('image_1_b')} rotation={photoRotations.image_1_b || 0} onRotate={() => rotatePhotoField('image_1_b')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
              </div>
              {/* Column 2: Bedroom */}
              <div className={styles.colMd3}>
                <div className={styles.formGroup}>
                  <label className={styles.controlLabel}>Name <span className={styles.required}>*</span></label>
                  <div className={styles.colSm10}>
                    <input type="text" name="image_2_name" value={formData.image_2_name || "Bedroom"} onChange={handleInputChange} className={styles.formControl} />
                  </div>
                </div>
                <PhotoUpload label="Bedroom" fieldName="image_2" onUpload={handlePhotoUpload} existingImage={formData.image_2} existingImageName={formData.image_2_name} existingImageUrl={formData.image_2_url} onPreviewClick={() => openInspectionGallery('image_2')} rotation={photoRotations.image_2 || 0} onRotate={() => rotatePhotoField('image_2')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
                <PhotoUpload label="Bedroom" fieldName="image_2_a" onUpload={handlePhotoUpload} existingImage={formData.image_2_a} existingImageUrl={formData.image_2_a_url} onPreviewClick={() => openInspectionGallery('image_2_a')} rotation={photoRotations.image_2_a || 0} onRotate={() => rotatePhotoField('image_2_a')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
                <PhotoUpload label="Bedroom" fieldName="image_2_b" onUpload={handlePhotoUpload} existingImage={formData.image_2_b} existingImageUrl={formData.image_2_b_url} onPreviewClick={() => openInspectionGallery('image_2_b')} rotation={photoRotations.image_2_b || 0} onRotate={() => rotatePhotoField('image_2_b')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
              </div>
              {/* Column 3: Kitchen/Bathroom */}
              <div className={styles.colMd3}>
                <div className={styles.formGroup}>
                  <label className={styles.controlLabel}>Name <span className={styles.required}>*</span></label>
                  <div className={styles.colSm10}>
                    <input type="text" name="image_3_name" value={formData.image_3_name || "Kitchen/Bathroom"} onChange={handleInputChange} className={styles.formControl} />
                  </div>
                </div>
                <PhotoUpload label="Kitchen/Bathroom" fieldName="image_3" onUpload={handlePhotoUpload} existingImage={formData.image_3} existingImageName={formData.image_3_name} existingImageUrl={formData.image_3_url} onPreviewClick={() => openInspectionGallery('image_3')} rotation={photoRotations.image_3 || 0} onRotate={() => rotatePhotoField('image_3')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
                <PhotoUpload label="Kitchen/Bathroom" fieldName="image_3_a" onUpload={handlePhotoUpload} existingImage={formData.image_3_a} existingImageUrl={formData.image_3_a_url} onPreviewClick={() => openInspectionGallery('image_3_a')} rotation={photoRotations.image_3_a || 0} onRotate={() => rotatePhotoField('image_3_a')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
                <PhotoUpload label="Kitchen/Bathroom" fieldName="image_3_b" onUpload={handlePhotoUpload} existingImage={formData.image_3_b} existingImageUrl={formData.image_3_b_url} onPreviewClick={() => openInspectionGallery('image_3_b')} rotation={photoRotations.image_3_b || 0} onRotate={() => rotatePhotoField('image_3_b')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
              </div>
              {/* Column 4: Communal Area */}
              <div className={styles.colMd3}>
                <div className={styles.formGroup}>
                  <label className={styles.controlLabel}>Name <span className={styles.required}>*</span></label>
                  <div className={styles.colSm10}>
                    <input type="text" name="image_4_name" value={formData.image_4_name || "Communal Area"} onChange={handleInputChange} className={styles.formControl} />
                  </div>
                </div>
                <PhotoUpload label="Communal Area" fieldName="image_4" onUpload={handlePhotoUpload} existingImage={formData.image_4} existingImageName={formData.image_4_name} existingImageUrl={formData.image_4_url} onPreviewClick={() => openInspectionGallery('image_4')} rotation={photoRotations.image_4 || 0} onRotate={() => rotatePhotoField('image_4')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
                <PhotoUpload label="Communal Area" fieldName="image_4_a" onUpload={handlePhotoUpload} existingImage={formData.image_4_a} existingImageUrl={formData.image_4_a_url} onPreviewClick={() => openInspectionGallery('image_4_a')} rotation={photoRotations.image_4_a || 0} onRotate={() => rotatePhotoField('image_4_a')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
                <PhotoUpload label="Communal Area" fieldName="image_4_b" onUpload={handlePhotoUpload} existingImage={formData.image_4_b} existingImageUrl={formData.image_4_b_url} onPreviewClick={() => openInspectionGallery('image_4_b')} rotation={photoRotations.image_4_b || 0} onRotate={() => rotatePhotoField('image_4_b')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
              </div>
            </div>
          </>
        ) : (
          /* Standard: 4 photos in 1 row */
          <div className={styles.row}>
            <div className={styles.colMd3}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>
                  Name <span className={styles.required}>*</span>
                </label>
                <div className={styles.colSm10}>
                  <input type="text" name="image_1_name" value={formData.image_1_name || "Front"} onChange={handleInputChange} className={styles.formControl} required />
                </div>
              </div>
              <PhotoUpload label="Front" fieldName="image_1" onUpload={handlePhotoUpload} existingImage={formData.image_1} existingImageName={formData.image_1_name} existingImageUrl={formData.image_1_url} onPreviewClick={() => openInspectionGallery('image_1')} rotation={photoRotations.image_1 || 0} onRotate={() => rotatePhotoField('image_1')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
            </div>
            <div className={styles.colMd3}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>
                  Name <span className={styles.required}>*</span>
                </label>
                <div className={styles.colSm10}>
                  <input type="text" name="image_2_name" value={formData.image_2_name || "Living Space"} onChange={handleInputChange} className={styles.formControl} required />
                </div>
              </div>
              <PhotoUpload label="Living Space" fieldName="image_2" onUpload={handlePhotoUpload} existingImage={formData.image_2} existingImageName={formData.image_2_name} existingImageUrl={formData.image_2_url} onPreviewClick={() => openInspectionGallery('image_2')} rotation={photoRotations.image_2 || 0} onRotate={() => rotatePhotoField('image_2')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
            </div>
            <div className={styles.colMd3}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>
                  Name <span className={styles.required}>*</span>
                </label>
                <div className={styles.colSm10}>
                  <input type="text" name="image_3_name" value={formData.image_3_name || "Kitchen/Bathroom"} onChange={handleInputChange} className={styles.formControl} required />
                </div>
              </div>
              <PhotoUpload label="Kitchen/Bathroom" fieldName="image_3" onUpload={handlePhotoUpload} existingImage={formData.image_3} existingImageName={formData.image_3_name} existingImageUrl={formData.image_3_url} onPreviewClick={() => openInspectionGallery('image_3')} rotation={photoRotations.image_3 || 0} onRotate={() => rotatePhotoField('image_3')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
            </div>
            <div className={styles.colMd3}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>
                  Name <span className={styles.required}>*</span>
                </label>
                <div className={styles.colSm10}>
                  <input type="text" name="image_4_name" value={formData.image_4_name || "Communal Area"} onChange={handleInputChange} className={styles.formControl} required />
                </div>
              </div>
              <PhotoUpload label="Communal Area" fieldName="image_4" onUpload={handlePhotoUpload} existingImage={formData.image_4} existingImageName={formData.image_4_name} existingImageUrl={formData.image_4_url} onPreviewClick={() => openInspectionGallery('image_4')} rotation={photoRotations.image_4 || 0} onRotate={() => rotatePhotoField('image_4')} onPreviewReady={handlePhotoPreviewReady} onOfflineSave={handleOfflineSave} />
            </div>
          </div>
        )}

        {/* Brief Section */}
        <div className={styles.section}>
          <h3>Brief</h3>
          <div className={styles.formGroup}>
            <label className={styles.controlLabel}>
              Brief Overview <span className={styles.required}>*</span>
            </label>
            <div className={styles.colSm10}>
            <textarea
              name="brief_overview"
              value={formData.brief_overview}
              onChange={handleInputChange}
              className={styles.formControl}
              rows={4}
              placeholder="Enter brief overview..."
                required
            />
            </div>
          </div>
        </div>

        {/* Dates Section */}
        <div className={styles.reportSectionHeader}>Dates</div>
        <div className={styles.row}>
          <div className={styles.colXs6}>
            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Inspection Date{" "}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6} style={{ position: "relative" }}>
                <input
                  type="text"
                  name="inspection_date"
                  placeholder="dd/mm/yyyy"
                  value={formData.inspection_date}
                  readOnly
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openDatePicker("inspection_date", e);
                  }}
                  className={styles.formControl}
                  required
                  style={{ paddingRight: "35px", cursor: "pointer", position: "relative", zIndex: 1 }}
                />
                {/* Hidden date input for native date picker */}
                <input
                  ref={inspectionDateInputRef}
                  type="date"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                    zIndex: 2,
                    fontSize: "16px",
                    padding: "6px 35px 6px 10px",
                    margin: 0,
                    border: "none",
                    background: "transparent",
                    boxSizing: "border-box",
                    pointerEvents: "auto",
                  }}
                  value={convertToDateInputFormat(formData.inspection_date)}
                  onChange={(e) => handleDateChange("inspection_date", e)}
                  onClick={(e) => {
                    // Allow direct click on hidden input to open picker
                    e.stopPropagation();
                    console.log('🔍 [DATE PICKER] Hidden input clicked directly');
                  }}
                />
                <span
                  className="glyphicon glyphicon-calendar"
                  onClick={(e) => openDatePicker("inspection_date", e)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#999",
                    cursor: "pointer",
                    pointerEvents: "auto",
                    zIndex: 5,
                  }}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Year <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>
          </div>

          <div className={styles.colXs6}>
            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Month <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <select
                  name="month"
                  value={formData.month}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Lease Start Date{" "}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6} style={{ position: "relative" }}>
                <input
                  type="text"
                  name="lease_start_date"
                  placeholder="dd/mm/yyyy"
                  value={formData.lease_start_date}
                  readOnly
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openDatePicker("lease_start_date", e);
                  }}
                  className={styles.formControl}
                  required
                  style={{ paddingRight: "35px", cursor: "pointer", position: "relative", zIndex: 1 }}
                />
                {/* Hidden date input for native date picker */}
                <input
                  ref={leaseStartDateInputRef}
                  type="date"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                    zIndex: 2,
                    fontSize: "16px",
                    padding: "6px 35px 6px 10px",
                    margin: 0,
                    border: "none",
                    background: "transparent",
                    boxSizing: "border-box",
                    pointerEvents: "auto",
                  }}
                  value={convertToDateInputFormat(formData.lease_start_date)}
                  onChange={(e) => handleDateChange("lease_start_date", e)}
                  onClick={(e) => {
                    // Allow direct click on hidden input to open picker
                    e.stopPropagation();
                    console.log('🔍 [DATE PICKER] Hidden input clicked directly');
                  }}
                />
                <span
                  className="glyphicon glyphicon-calendar"
                  onClick={(e) => openDatePicker("lease_start_date", e)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#999",
                    cursor: "pointer",
                    pointerEvents: "auto",
                    zIndex: 5,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Meters Section */}
        <div className={styles.reportSectionHeader}>Meters</div>

        {useNewMeterRegister ? (
          <>
          {editableMeters.length === 0 ? (
            <p style={{ margin: "4px 0 16px", fontSize: "13px", color: "#a94442" }}>
              No meters are set up for this property yet. Add them on the property
              page before completing this inspection.
            </p>
          ) : (
            <>
            <p style={{ margin: "4px 0 10px", fontSize: "13px", color: "#6c757d" }}>
              Meters come from the property record. Enter the reading and upload a
              photo for each. To add or remove a meter, use the property page.
            </p>
            <div className={styles.row}>
              {editableMeters.map((meter) => {
                const photoField = 'meter_photo_' + meter.id;
                return (
                  <div className={styles.colMd3} key={meter.id}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>
                        {meter.meter_type}
                      </label>
                      <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "8px" }}>
                        Serial {meter.serial_number}
                        <br />
                        {meter.location}
                      </div>
                      <label className={styles.controlLabel}>
                        Reading{' '}
                        {requiredMeterIds.includes(meter.id) ? (
                          <span className={styles.required}>*</span>
                        ) : null}
                      </label>
                      {!requiredMeterIds.includes(meter.id) && (
                        <div style={{ fontSize: "11px", color: "#8a6d3b", marginBottom: "4px" }}>
                          Added to the property after this report &mdash; leave
                          blank unless you have a reading for this month.
                        </div>
                      )}
                      <input
                        type="text"
                        className={styles.formControl}
                        maxLength={255}
                        value={formData.meter_readings?.[meter.id] || ''}
                        onChange={(e) =>
                          handleMeterReadingChange(meter.id, e.target.value)
                        }
                        required
                      />
                    </div>
                    <PhotoUpload
                      label={meterPhotoLabels[photoField] || meter.meter_type}
                      fieldName={photoField}
                      onUpload={handlePhotoUpload}
                      existingImage={formData[photoField]}
                      existingImageUrl={formData[photoField + '_url']}
                      onPreviewClick={() => openMeterGallery(photoField)}
                      rotation={photoRotations[photoField] || 0}
                      onRotate={() => rotatePhotoField(photoField)}
                      onPreviewReady={handlePhotoPreviewReady}
                      onOfflineSave={handleOfflineSave}
                    />
                  </div>
                );
              })}
            </div>
            </>
          )}
          </>
        ) : (
          <>
        {/* Type new reading type - positioned at top right like Laravel */}
        <div className={styles.row} style={{ marginBottom: "15px", justifyContent: "flex-end" }}>
          <div className={styles.colSm3}>
            <div className={styles.entry}>
              <input
                id="new_field_input"
                className={styles.formControl}
                type="text"
                placeholder="Type new reading type"
                value={newMeterType}
                onChange={(e) => setNewMeterType(e.target.value)}
              />
              <button
                id="new_field_button"
                className={styles.btnAdd}
                type="button"
                onClick={handleAddMeter}
              >
                <span className={styles.glyphicon}>+</span>
              </button>
            </div>
          </div>
        </div>

        <div id="js-monthly-inspection-report-meters-form">
          {formData.reading_fields.map((reading, index) => (
            <div key={index} className={styles.row}>
              <input
                type="hidden"
                name={`reading_fields_names[${index}]`}
                value={formData.reading_fields_names[index]}
              />
              <div className={styles.colSm1}>
                <button
                  type="button"
                  onClick={() => handleRemoveMeter(index)}
                  className={styles.btnRemove}
                >
                  Delete
                </button>
              </div>
              <div className={styles.colSm5}>
                <div className={styles.formGroup}>
                  <label className={styles.controlLabel}>
                    Reading {formData.reading_fields_names[index]}{" "}
                    <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.colSm6}>
                    <input
                      type="text"
                      name={`reading_fields[${index}]`}
                      value={reading}
                      onChange={(e) =>
                        handleMeterChange(
                          index,
                          "reading_fields",
                          e.target.value
                        )
                      }
                      className={styles.formControl}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className={styles.colSm6}>
                <div className={styles.formGroup}>
                  <label className={styles.controlLabel}>
                    Reading {formData.reading_fields_names[index]}{" "}
                    Serial <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.colSm6}>
                    <input
                      type="text"
                      name={`reading_fields_serials[${index}]`}
                      value={formData.reading_fields_serials[index]}
                      onChange={(e) =>
                        handleMeterChange(
                          index,
                          "reading_fields_serials",
                          e.target.value
                        )
                      }
                      className={styles.formControl}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Meters Photos Section - shown for all clients, MTVH included */}
        <>
            <div className={styles.reportSectionHeader}>Meters Photos</div>
            <p style={{ margin: "4px 0 10px", fontSize: "13px", color: "#6c757d" }}>
              You can add up to {METER_PHOTO_POOL.length} meter photos.
            </p>
            <div
              className={styles.row}
              style={{ marginBottom: "10px", justifyContent: "flex-end" }}
            >
              <button
                type="button"
                className={styles.btnAdd}
                onClick={handleAddMeterPhoto}
                disabled={meterSlots.length >= METER_PHOTO_POOL.length}
                title={
                  meterSlots.length >= METER_PHOTO_POOL.length
                    ? "Maximum of 10 meter photos reached"
                    : "Add another meter photo"
                }
              >
                <span className={styles.glyphicon}>+</span> Add photo
              </button>
            </div>
            <div className={styles.row}>
              {meterSlots.map((field) => (
                <div className={styles.colMd3} key={field}>
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Name <span className={styles.required}>*</span>
                      {meterSlots.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveMeterPhoto(field)}
                          className={styles.btnRemove}
                          style={{ marginLeft: "8px" }}
                        >
                          Delete
                        </button>
                      )}
                    </label>
                    <div className={styles.colSm10}>
                      <input
                        type="text"
                        name={`${field}_name`}
                        value={
                          formData[`${field}_name`] ||
                          METER_DEFAULT_NAMES[field] ||
                          ""
                        }
                        onChange={handleInputChange}
                        className={styles.formControl}
                        placeholder="Meter name"
                        required
                      />
                    </div>
                  </div>
                  <PhotoUpload
                    label={
                      formData[`${field}_name`] ||
                      METER_DEFAULT_NAMES[field] ||
                      "Meter"
                    }
                    fieldName={field}
                    onUpload={handlePhotoUpload}
                    existingImage={formData[field]}
                    existingImageName={formData[`${field}_name`]}
                    existingImageUrl={formData[`${field}_url`]}
                    onPreviewClick={() => openMeterGallery(field)}
                    rotation={photoRotations[field] || 0}
                    onRotate={() => rotatePhotoField(field)}
                    onPreviewReady={handlePhotoPreviewReady}
                    onOfflineSave={handleOfflineSave}
                  />
                </div>
              ))}
            </div>
        </>
          </>
        )}

        {/* Tickets Section - Hidden for MTVH properties */}
        {!isMetropolitanHousing && (
          <div className={styles.section}>
            <h3>Tickets</h3>
            <TicketsTable propertyId={propertyId} month={formData.month} year={formData.year} />
          </div>
        )}

        {/* Other Fields Section */}
        <div className={styles.row}>
          <div className={styles.colXs12}>
            <div className={styles.reportSectionHeader}>Other</div>
          </div>

          <div className={styles.colXs6}>
            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Client <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="client"
                  value={formData.client}
                  onChange={handleInputChange}
                  className={styles.formControl}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Contact Person{" "}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="client_contact"
                  value={formData.client_contact}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Property ID <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="property_id_name"
                  value={formData.property_id_name}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>Address 2</label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="address_2"
                  value={formData.address_2}
                  onChange={handleInputChange}
                  className={styles.formControl}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Postcode <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Number of Guardians in Occupation{" "}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm3}>
                <input
                  type="number"
                  name="guardians_number"
                  value={formData.guardians_number}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>
          </div>

          <div className={styles.colXs6}>
            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Account Manager{" "}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="account_manager"
                  value={formData.account_manager}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Account Manager Email{" "}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="email"
                  name="account_manager_email"
                  value={formData.account_manager_email}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Contact First Name{" "}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="client_contact_short"
                  value={formData.client_contact_short}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Address 1 <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="address_1"
                  value={formData.address_1}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>Address 3</label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="address_3"
                  value={formData.address_3}
                  onChange={handleInputChange}
                  className={styles.formControl}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Town <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="town"
                  value={formData.town}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Savings Made <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="text"
                  name="savings_made"
                  value={formData.savings_made}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>
                Amount of Months Since Lease Start Date{" "}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.colSm6}>
                <input
                  type="number"
                  name="months_under_management"
                  value={formData.months_under_management}
                  onChange={handleInputChange}
                  className={styles.formControl}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>Property date live</label>
              <div className={styles.colSm6}>
                <label className={styles.controlLabel}>
                  {formData.date_live
                    ? new Date(formData.date_live).toLocaleDateString("en-GB")
                    : "N/A"}
                </label>
              </div>
            </div>

          <div className={styles.formGroup}>
              <label className={styles.controlLabel}>Today</label>
              <div className={styles.colSm6}>
                <label className={styles.controlLabel}>
                  {new Date().toLocaleDateString("en-GB")}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Inspection Checklist - Different layout for MTVH vs Standard */}
        <ChecklistGroup
          title="Monthly Inspection Checklist"
          sections={isMetropolitanHousing ? {
            // MTVH simplified checklist - single item per category
            external: {
              title: "External",
              items: ["External"],
            },
            location: {
              title: "Communal Area",
              items: ["Communal Area"],
            },
            kitchen: {
              title: "Kitchen",
              items: ["Kitchen"],
            },
            living_space: {
              title: "Bedroom",
              items: ["Bedroom"],
            },
            bathroom: {
              title: "Bathroom",
              items: ["Bathroom"],
            },
            other: {
              title: "Other",
              items: ["Other"],
            },
          } : {
            // Standard detailed checklist
            external: {
              title: "External",
              items: [
                "Front Door",
                "Back Door",
                "Other Exit Door",
                "External Walls",
                "Windows",
                "Garage/Shed",
                "Lawn",
                "Bin(s)",
                "Exits Clear",
                "Other",
              ],
            },
            kitchen: {
              title: "Kitchen",
              items: [
                "Door(s)",
                "Wall",
                "Ceiling",
                "Window(s)",
                "Floor",
                "Light(s)",
                "Washing Machine",
                "Hot plate",
                "Microwave",
                "Bin(s)",
                "Sink",
                "Plug socket(s)",
                "Fire extinguisher(s)",
                "Fire blanket",
                "Other",
              ],
            },
            living_space: {
              title: "Living Space",
              items: [
                "Door",
                "Wall",
                "Ceiling",
                "Window(s)",
                "Floor",
                "Light(s)",
                "Plug socket(s)",
                "Fire extinguisher(s)",
                "Fire blanket",
                "Other",
              ],
            },
            bathroom: {
              title: "Bathroom",
              items: [
                "Door",
                "Wall",
                "Ceiling",
                "Window",
                "Floor",
                "Light",
                "Plug socket(s)",
                "Water",
                "Shower/cubicle",
                "Bath",
                "Sink",
                "Extractor fan",
                "Other",
              ],
            },
            other: {
              title: "Other",
              items: [
                "Emergency lighting",
                "Alarms",
                "Toilet",
                "Roof",
                "Stairwells - Clear",
                "Landings - Clear",
                "Plug socket(s)",
              ],
            },
            health_and_safety: {
              title: "Health and Safety",
              items: [
                "Unused water outlets run for 2 minutes",
                "Obstacles",
                "Electric voltage over 13 amps",
                "Other",
              ],
            },
          }}
          locations={locations}
          onLocationAdd={handleAddLocation}
          onLocationRemove={handleRemoveLocation}
          newLocation={newLocation}
          setNewLocation={setNewLocation}
          data={formData}
          onChange={handleChecklistChange}
          isMetropolitan={isMetropolitanHousing}
          showLocations={!isMetropolitanHousing}
          ticketTypes={ticketTypes}
          monthlyInspectionResponses={monthlyInspectionResponses}
          onOfflineSave={handleOfflineSave}
        />

        {/* Signature Section - Hidden for MTVH properties */}
        {!isMetropolitanHousing && (
          <div className={styles.section}>
            <h3>Signature</h3>
            {console.log('🔍 [DEBUG] Rendering SignatureCanvas with value:', formData.e_sign ? formData.e_sign.substring(0, 50) + '...' : 'null')}
            <SignatureCanvas onChange={handleSignature} value={formData.e_sign} />
          </div>
        )}

        {/* Form Actions */}
        <div className={styles.formActions}>
          <button
            type="button"
            onClick={() => navigate(`/properties/${propertyId}`)}
            className={`${styles.btn} ${styles.btnSecondary}`}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || meterRegisterMissing}
            className={`${styles.btn} ${styles.btnPrimary}`}
          >
            {submitting ? 'Updating...' : 'Update Report'}
          </button>
        </div>
      </form>

      {/* Large image viewer — inspection photos (controlled rotation so the
          slot thumbnail tracks the lightbox rotation). */}
      <PhotoGallery
        open={galleryOpen}
        close={() => setGalleryOpen(false)}
        slides={inspectionGallerySlides}
        index={galleryIndex}
        rotations={inspectionGalleryRotations}
        onRotate={handleInspectionGalleryRotate}
      />

      {/* Large image viewer — meter photos (separate set so prev/next stays in meters) */}
      <PhotoGallery
        open={meterGalleryOpen}
        close={() => setMeterGalleryOpen(false)}
        slides={meterGallerySlides}
        index={meterGalleryIndex}
        rotations={meterGalleryRotations}
        onRotate={handleMeterGalleryRotate}
      />
    </div>
  );
};

export default MonthlyInspectionEdit;

